import { postData } from "./backend";
import { initI18n } from "./i18n";
import { useCallback, useEffect, useState } from "react";
import { Workflow } from "./interface";
import InstantWorkflowCard from "./InstantWorkflowCard";
import { useTranslation } from "react-i18next";

initI18n();

interface Props {
  token: string;
  prompt: string;
  onSelectWorkflow: (slug: string) => void;
  instantWorkflows: Array<Workflow>;
}

const CLANKING_SPEED = 75;

const CONFIDENCE_RESPONSE: Record<string, string> = {
  0: "I couldn't find a strong match for the workflow you are looking for but here are some others instead.",
  1: "The following workflow might be a good match.",
  2: "Perfect, it looks like we have the workflow you are looking for.",
}

function AppOnboardingWorkflowsChat(props: Props) {
  const { t } = useTranslation();

  const { token, prompt } = props;

  const [response, setResponse] = useState("");
  const [workflow, setWorkflow] = useState<Workflow>();

  const [showActions, setShowActions] = useState(false);
  const [showAllOptions, setShowAllOptions] = useState(false);
  const [confidence, setConfidence] = useState<number>(-1);

  const [isLoading, setIsLoading] = useState(false);

  const typeWordAndNext = useCallback((words: Array<string>, index: number, onComplete: () => void) => {
    if (index >= words.length)
      return onComplete();

    setResponse(r => r + " " + words[index]);
    setTimeout(() => typeWordAndNext(words, index + 1, onComplete), CLANKING_SPEED);
  }, []);

  const clankOutResponse = useCallback((text: string) => {
    setResponse("");

    const words = text.split(" ");
    typeWordAndNext(words, 0, () => setShowActions(true));

  }, [typeWordAndNext]);

  const submitChat = useCallback(() => {
    setIsLoading(true);

    postData("/hubspot/onboarding/chat/", token, { prompt })
      .then(data => {
        const { workflow, confidenceInterval } = data;
        setWorkflow(workflow);
        setConfidence(confidenceInterval);

        const response = CONFIDENCE_RESPONSE[confidenceInterval];
        clankOutResponse(response);
      })
      .finally(() => setIsLoading(false));
  }, [clankOutResponse, token, prompt]);

  useEffect(() => {
    submitChat();
  }, [submitChat]);

  return (
    <div className="flex flex-col h-full w-[800px] gap-4">
      <div className="bg-gray-300 px-6 py-2 rounded-full ml-auto max-w-[600px] font-medium">
        {props.prompt}
      </div>
      <div className="py-4 max-w-[600px] font-medium">
        {isLoading
          ? <div className="w-5 h-5 bg-blue rounded-full animate-pulse" />
          : response
        }
      </div>
      {showActions && confidence >= 1 && workflow && !showAllOptions &&
        <div className="flex flex-col w-full">
          <InstantWorkflowCard workflow={workflow} onSelect={(wkf) => props.onSelectWorkflow(wkf.publicWorkflowSlug)} />
        </div>
      }
      {showActions && confidence === 1 && !showAllOptions &&
        <button className="flex gap-4 underline cursor-pointer" onClick={() => setShowAllOptions(true)}>
          {t("Show all options")}
        </button>
      }
      {showActions && (confidence === 0 || showAllOptions) &&
        <div className="flex gap-4">
          {props.instantWorkflows.map((workflow) => <InstantWorkflowCard key={workflow.id} workflow={workflow} onSelect={(wkf) => props.onSelectWorkflow(wkf.publicWorkflowSlug)} />)}
        </div>
      }
    </div>
  );
}

export default AppOnboardingWorkflowsChat;
